import React from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand } from "@/components/ui-bits/Primitives";
import LeadForm from "@/components/forms/LeadForm";
import { Phone, Mail, MapPin, Calendar } from "lucide-react";
import { COMPANY, IMAGES } from "@/lib/site-data";

const FIELDS = [
    { name: "name", label: "Full Name", required: true },
    { name: "company", label: "Company" },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "phone", label: "Phone" },
    { name: "subject", label: "Subject", colSpan: 2, as: "select", options: ["Schedule Consultation", "Request Site Assessment", "Grant Assistance", "Request Proposal", "Fleet Inquiry", "Press / Media", "Other"] },
    { name: "message", label: "Message", as: "textarea", colSpan: 2, required: true },
];

const TexasMap = () => (
    <div className="relative aspect-[5/4] bg-ink-700/40 border border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <svg viewBox="0 0 500 460" className="absolute inset-0 w-full h-full p-6" xmlns="http://www.w3.org/2000/svg" aria-label="Texas service map">
            {/* Stylized Texas outline */}
            <path
                d="M80 70 L200 60 L210 80 L260 80 L270 60 L380 70 L420 110 L420 200 L380 240 L370 290 L340 330 L300 360 L280 420 L240 440 L210 410 L180 380 L150 380 L120 360 L100 320 L80 290 L70 240 L60 200 L70 150 Z"
                fill="rgba(0, 212, 255, 0.08)"
                stroke="rgba(0, 212, 255, 0.4)"
                strokeWidth="1.5"
            />
            {/* Cities */}
            {[
                { x: 245, y: 158, name: "Frisco (HQ)", primary: true },
                { x: 250, y: 165, name: "Dallas" },
                { x: 220, y: 175, name: "Fort Worth" },
                { x: 215, y: 305, name: "Austin" },
                { x: 195, y: 350, name: "San Antonio" },
                { x: 315, y: 290, name: "Houston" },
                { x: 105, y: 215, name: "Lubbock" },
                { x: 145, y: 405, name: "Laredo" },
                { x: 350, y: 405, name: "Corpus Christi" },
            ].map((c) => (
                <g key={c.name}>
                    <circle cx={c.x} cy={c.y} r={c.primary ? 7 : 4} fill={c.primary ? "#00D4FF" : "rgba(0,212,255,0.6)"} />
                    {c.primary && <circle cx={c.x} cy={c.y} r={14} fill="none" stroke="#00D4FF" strokeWidth="1" className="animate-pulse-slow" />}
                    <text x={c.x + 12} y={c.y + 4} fill="#94a3b8" fontSize="11" fontFamily="IBM Plex Sans">{c.name}</text>
                </g>
            ))}
        </svg>
        <div className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.22em] text-slate-500">Service Area · Texas</div>
    </div>
);

export default function Contact() {
    return (
        <div data-testid="contact-page">
            <PageHero
                kicker="Contact"
                title="Let's talk infrastructure."
                lead="Whether you're a property owner, fleet operator, municipality, or grant agency — we want to hear about your project."
                image={IMAGES.cityNight}
                height="h-[45vh] min-h-[360px]"
            />

            <Section>
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-7">
                            <Card hover={false} className="!p-8 md:!p-10">
                                <Eyebrow className="mb-2">Send a message</Eyebrow>
                                <h2 className="font-heading text-3xl text-white tracking-tight mb-8">How can we help?</h2>
                                <LeadForm type="contact" fields={FIELDS} submitLabel="Send Message" testIdPrefix="contact" />
                            </Card>
                        </div>
                        <div className="lg:col-span-5 space-y-6">
                            <TexasMap />
                            <Card hover={false} className="!p-6 space-y-4">
                                <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-electric-400 mt-0.5" /><div><div className="text-xs uppercase tracking-[0.18em] text-slate-400">Headquarters</div><div className="text-white">{COMPANY.city}</div></div></div>
                                <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-electric-400 mt-0.5" /><div><div className="text-xs uppercase tracking-[0.18em] text-slate-400">Phone</div><a data-testid="contact-phone-link" href={`tel:${COMPANY.phone}`} className="text-white hover:text-electric-400">{COMPANY.phone}</a></div></div>
                                <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-electric-400 mt-0.5" /><div><div className="text-xs uppercase tracking-[0.18em] text-slate-400">Email</div><a data-testid="contact-email-link" href={`mailto:${COMPANY.email}`} className="text-white hover:text-electric-400 break-all">{COMPANY.email}</a></div></div>
                                <div className="flex items-start gap-3"><Calendar className="w-5 h-5 text-electric-400 mt-0.5" /><div><div className="text-xs uppercase tracking-[0.18em] text-slate-400">Schedule a Call</div><a data-testid="contact-schedule-link" href={`mailto:${COMPANY.email}?subject=Consultation`} className="text-white hover:text-electric-400">Request via email</a></div></div>
                            </Card>
                        </div>
                    </div>
                </Container>
            </Section>
        </div>
    );
}
